import moment from 'moment';
import RoleViewManager from '../../RoleViewManager';
import {
  DelegateAssignmentOfExam,
  Exam,
  ExamDiploma,
  PassedParticipantsContext,
  SwimmingLessonProviderWithLessonLocations,
  User,
} from '../../types';
import { formatTimestamp } from '../../utils/common';
import { ExamDiplomaDTO, ExamDTO } from './repository/ExamRepository';
import { ExamFormState } from './types';

export const examLineToExamLineDTO = (
  examLine: ExamDiploma,
  context?: PassedParticipantsContext,
): ExamDiplomaDTO => {
  let numberPassed = 0;
  if (context === 'examiner') {
    numberPassed = examLine.numberPassedAccordingExaminer;
  }
  if (context === 'delegate') {
    numberPassed = examLine.numberPassedAccordingDelegate;
  }

  return {
    id: examLine.id,
    diplomaTypeId: examLine.diplomaType.id,
    examinerId: examLine?.examiner?.id,
    beginTime: new Date(examLine.beginTime),
    endTime: new Date(examLine.endTime),
    amount: examLine.numberParticipants,
    numberPassed,
  };
};

export const examToExamDTO = (exam: Exam): ExamDTO => ({
  id: exam.id,
  swimmingLessonProviderId: exam.swimmingLessonProvider.id,
  examNumber: exam.examNumber,
  date: new Date(exam.date),
  examLocationId: exam.examLocation.id,
  lessonLocationIds: exam.lessonLocations.map((l) => l.id),
  oneExaminerOverall: exam?.examiner !== undefined,
  examinerId: exam?.examiner?.id || '',
  diplomas: exam.examLines.map((examLine) => examLineToExamLineDTO(examLine)),
  menOnly: exam.menOnly,
  womenOnly: exam.womenOnly,
});

export const moreThanOneDayBeforeExam = (exam: Exam) => {
  const tomorrow = new Date().setDate(new Date().getDate() + 1);

  return (
    moment(tomorrow).format('YYYYMMDD') < moment(exam.date).format('YYYYMMDD')
  );
};

export const moreThan1hBeforeExam = (exam: Exam) => {
  const oneHourFromNow = new Date().setHours(new Date().getHours() + 1);

  return (
    moment(oneHourFromNow).format('YYYYMMDDHHmmss') <
    moment(exam.date).format('YYYYMMDDHHmmss')
  );
};

export const moreThan24hBeforeExam = (exam: Exam) => {
  const tomorrow = new Date().setDate(new Date().getDate() + 1);

  return (
    moment(tomorrow).format('YYYYMMDDHHmmss') <
    moment(exam.date).format('YYYYMMDDHHmmss')
  );
};

export const examHasStarted = (exam: Exam) => {
  return (
    moment(exam.date).format('YYYYMMDD') <=
    moment(new Date()).format('YYYYMMDD')
  );
};

/**
 * An exam is considered to be in the past once its date is on a calendar day
 * before today; an exam that started earlier today does not yet qualify. Mirrors
 * the back-end rule for closing an exam.
 */
export const examIsInThePast = (exam: Exam) => {
  return (
    moment(exam.date).format('YYYYMMDD') < moment(new Date()).format('YYYYMMDD')
  );
};

export const delegateAssignmentToText = (
  delegateAssignment: DelegateAssignmentOfExam,
) => {
  let status;
  let timestamp;
  switch (delegateAssignment.status) {
    case 'cancelled': {
      status = 'Geannuleerd';
      timestamp = delegateAssignment?.cancelledAt
        ? formatTimestamp(new Date(delegateAssignment.cancelledAt))
        : 'N.B.';
      break;
    }
    case 'confirmed': {
      status = 'Bevestigd';
      timestamp = delegateAssignment?.confirmedAt
        ? formatTimestamp(new Date(delegateAssignment.confirmedAt))
        : 'N.B.';
      break;
    }
    case 'declined': {
      status = 'Geweigerd';
      timestamp = delegateAssignment?.declinedAt
        ? formatTimestamp(new Date(delegateAssignment.declinedAt))
        : 'N.B.';
      break;
    }
    case 'invited': {
      status = 'Uitgenodigd';
      timestamp = delegateAssignment?.invitedAt
        ? formatTimestamp(new Date(delegateAssignment.invitedAt))
        : 'N.B.';
      break;
    }
    default: {
      return 'N.B.';
    }
  }

  return `${status} op ${timestamp}`;
};

export const allowedDelegateAssignmentActions = (
  delegateAssignment: DelegateAssignmentOfExam,
) => {
  let canCancel = false;
  let canConfirm = false;
  let canDecline = false;
  let canDelete = true;
  switch (delegateAssignment.status) {
    case 'confirmed': {
      canCancel = true;
      break;
    }
    case 'invited': {
      canConfirm = true;
      canDecline = true;
      break;
    }
    default: {
      break;
    }
  }

  return {
    canCancel,
    canConfirm,
    canDecline,
    canDelete,
  };
};

export const getExamPermissions = (
  exam: Exam | null,
  account: User | null,
  roleViewManager: RoleViewManager,
) => {
  if (!exam || account === null) {
    return {
      canEdit: false,
      canDelete: false,
      canRegister: false,
      canUpdateAttendance: false,
      canViewDelegate: false,
      canClose: false,
    };
  }

  const inAdminView = roleViewManager.isAdminView();
  const inSwimmingLessonProviderView =
    roleViewManager.isSwimmingLessonProviderView();
  const inDelegateView = roleViewManager.isDelegateView();

  const moreThan1hBefore = moreThan1hBeforeExam(exam);
  const moreThan24hBefore = moreThan24hBeforeExam(exam);
  const moreThanOneDayBefore = moreThanOneDayBeforeExam(exam);

  const canEdit =
    inAdminView || (inSwimmingLessonProviderView && moreThan1hBefore);
  const canDelete = canEdit;
  const canRegister =
    inDelegateView &&
    moreThan24hBefore &&
    exam.confirmedDelegates?.length === 0;
  const canUpdateAttendance =
    inDelegateView && moreThan24hBefore && exam.delegate?.id === account.id;
  const canViewDelegate =
    inAdminView || inDelegateView || !moreThanOneDayBefore;
  const canClose =
    account.roles.includes('ROLE_SUPER_ADMIN') &&
    examIsInThePast(exam) &&
    !exam.closedAt &&
    exam.examinerFormEntry?.state !== ExamFormState.FINALIZED;

  return {
    canEdit,
    canDelete,
    canRegister,
    canUpdateAttendance,
    canViewDelegate,
    canClose,
  };
};

export const requireLessonLocationsForExam = (
  swimmingLessonProvider: SwimmingLessonProviderWithLessonLocations,
) =>
  swimmingLessonProvider.type === 'regular_swimming_lesson_provider' &&
  !swimmingLessonProvider.centralExams;
